import type { Car, PriceAnalysis, TimeToSellData } from 'services/types/car';
import type { Client } from 'services/types/client';

import baseApi from './baseApi';

interface CarAggregationEntry {
  value: string;
  count: number;
}

interface CarModelAggregationEntry extends CarAggregationEntry {
  brand: string;
}

interface CarsResponse {
  cars: Car[];
  total: number;
  aggregations: {
    brands: CarAggregationEntry[];
    models: CarModelAggregationEntry[];
  };
}

const carsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCars: build.query<
      CarsResponse,
      {
        businessUnitId?: number;
        isUsed?: string;
        paginationModel?: string;
        filterModel?: string;
        sortModel?: string;
        searchTerm?: string;
      }
    >({
      query: (params) => ({
        url: `/cars?${new URLSearchParams({
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId.toString() } : {}),
          ...(params.isUsed ? { isUsed: params.isUsed } : {}),
          ...(params.paginationModel ? { paginationModel: params.paginationModel } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.sortModel ? { sortModel: params.sortModel } : {}),
          ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
        }).toString()}`,
        method: 'GET',
      }),
      transformResponse: (response: CarsResponse) => response,
    }),
    getCar: build.query({
      query: (id: number) => ({
        url: `/cars/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Car) => response,
    }),
    updateCar: build.mutation({
      query: (params: { id: number; body: Partial<Car> }) => ({
        url: `/cars/${params.id}`,
        method: 'PATCH',
        body: params.body,
      }),
      transformResponse: (response: Car) => response,
    }),
    getRecommendedClients: build.mutation({
      query: (params: { carId: number; aiAnalysisLimit?: number; pageIndex?: number }) => ({
        url: `/admin/cars/${params.carId}/recommend-to-clients`,
        method: 'POST',
        body: {
          aiAnalysisLimit: params.aiAnalysisLimit,
          pageIndex: params.pageIndex,
        },
      }),
      transformResponse: (response: {
        car: Car;
        analyzedClientsCount: number;
        recommendedClientsCount: number;
        recommendedClients: Client[];
      }) => response,
    }),
    getPriceAnalysis: build.mutation({
      query: (params: { carId: number }) => ({
        url: `/admin/cars/${params.carId}/price-analysis`,
        method: 'POST',
        body: { chileAutosLimit: 20 },
      }),
      transformResponse: (response: PriceAnalysis) => response,
    }),
    getTimeToSell: build.query({
      query: (params: { businessUnitId: number }) => ({
        url: `/admin/cars/timeToSell`,
        method: 'POST',
        body: { businessUnitId: params.businessUnitId },
      }),
      transformResponse: (response: TimeToSellData) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCarsQuery,
  useGetCarQuery,
  useGetRecommendedClientsMutation,
  useGetPriceAnalysisMutation,
  useGetTimeToSellQuery,
  useUpdateCarMutation,
} = carsApi;
